import { dateFormat } from './dateFunctions';

const formatWarehouse = (w) => {
    if (!w) return '';
    if (w.isFarm) return w.name || w.description || 'Farm';
    return w.description || w.name || '';
};

const formatLease = (lease) => {
    if (!lease) return '';
    const name = lease.displayName || lease.name || lease.leaseNo || lease.number || '';
    const number = lease.number && (!name || name === lease.name) ? lease.number : null;
    if (name && number && !name.includes(number)) {
        return `${name} (${number})`;
    }
    return name || number || '';
};

const formatEstuary = (lease) => lease?.harvestArea?.estuary?.description || lease?.estuary?.description || lease?.estuary || '';
const formatHarvestArea = (lease) => lease?.harvestArea?.description || lease?.harvestArea || '';

const aggregateLines = (list) => {
    if (!Array.isArray(list)) return [];
    const groups = new Map();
    list.forEach((l) => {
        const key = l?.mixedGroupId || l?.MixedGroupId || `__single_${l.id || Math.random()}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(l);
    });

    const aggregated = [];
    groups.forEach((items, key) => {
        if (key.startsWith('__single_') || items.length === 1) {
            const it = items[0];
            aggregated.push(it);
            return;
        }

        const first = items[0] || {};
        const totalBins = items.reduce((p, c) => p + (Number(c.bins) || 0), 0);
        const totalBags = items.reduce((p, c) => p + (Number(c.bags) || 0), 0);
        const totalQty = items.reduce((p, c) => p + (Number(c.quantity) || 0), 0);
        const breakdown = items.map((it) => ({
            grade: it?.stockItem?.grade?.description || it?.stockItem?.description || 'Unknown',
            qty: Number(it?.quantity) || 0,
        }));

        aggregated.push({
            ...first,
            mixedGroupId: first?.mixedGroupId || first?.MixedGroupId || key,
            bins: totalBins,
            bags: totalBags,
            quantity: totalQty,
            __mixedGroupBreakdown: breakdown,
        });
    });
    return aggregated;
};

export const buildOtrPdfPayload = (transfer, customers = []) => {
    if (!transfer) {
        return { pdfData: null, oysters: [] };
    }

    const getCustomerName = (customerId) => {
        if (!customerId || !customers?.length) return '';
        const match = customers.find((c) => c.id === customerId);
        return match ? match.name : '';
    };

    const lines = transfer.coreTransferLines || [];
    const leaseMap = new Map();
    const cropMap = new Map();

    const oysters = lines.map((line) => {
        const lease = line?.group?.previousLocation?.line?.lease;
        if (lease) {
            const key = lease.id || `${lease.name || ''}-${lease.number || ''}`;
            if (!leaseMap.has(key)) {
                leaseMap.set(key, lease);
            }
        }
        if (line?.crop) {
            cropMap.set(line.crop, true);
        }

        const leaseEstuary = formatEstuary(lease);
        const leaseHarvestArea = formatHarvestArea(lease);

        return {
            ...line,
            bags: line?.bags ?? 0,
            bins: line?.bins ?? 0,
            quantity: line?.quantity ?? 0,
            crop: line?.crop || '',
            lease: lease
                ? {
                    ...lease,
                    displayName: formatLease(lease),
                    estuary: leaseEstuary,
                    harvestArea: leaseHarvestArea,
                }
                : undefined,
        };
    });

    const leases = Array.from(leaseMap.values());
    const leaseNames = leases.map(formatLease).filter(Boolean);
    const cropYears = Array.from(cropMap.keys());

    const multipleLeases = leaseNames.length > 1;
    const multipleCropYear = cropYears.length > 1;
    const primaryLease = leases[0];
    const estuaryName = formatEstuary(primaryLease);
    const harvestAreaName = formatHarvestArea(primaryLease);
    const isFarmFrom = transfer?.source?.isFarm ?? false;
    const isFarmTo = transfer?.destination?.isFarm ?? false;

    const pdfData = {
        id: transfer.id,
        date: transfer.dateReady ? dateFormat(new Date(transfer.dateReady)) : dateFormat(new Date()),
        OTR: transfer.externalOTR || transfer.ExternalOTR || transfer.otr || transfer.OTR || '',
        PRN: transfer.prn || transfer.PRN || '',
        IVR: transfer.ivr || transfer.IVR || 0,
        comment: transfer.comment || transfer.Comment || transfer.comments || '',
        HarvestDate: transfer.harvestDate ? dateFormat(new Date(transfer.harvestDate)) : '',
        multipleLeases,
        multipleCropYear,
        Estuary: estuaryName,
        HarvestArea: harvestAreaName,
        Lease: multipleLeases ? undefined : formatLease(primaryLease),
        CropYear: multipleCropYear ? undefined : (cropYears[0] || transfer.crop || ''),
        from: formatWarehouse(transfer.source),
        to: transfer.isCustomer
            ? (getCustomerName(transfer.customerId) || formatWarehouse(transfer.destination))
            : formatWarehouse(transfer.destination),
        isFarmFrom,
        isFarmTo,
    };

    return {
        pdfData,
        oysters: aggregateLines(oysters),
    };
};
